import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Info, Users } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Equipe() {
  const [members, setMembers] = useState([]);
  const [limit, setLimit] = useState(-1);
  const [used, setUsed] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("tecnico");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/team").then((r) => {
    setMembers(r.data.members || []);
    setLimit(r.data.limit);
    setUsed(r.data.used);
  }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/team", { name, email, role });
      toast.success("Membro adicionado! Ele deve criar a conta dele no cadastro com este mesmo e-mail.");
      setName(""); setEmail(""); setRole("tecnico");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (m, newRole) => {
    try {
      await api.put(`/team/${m.id}`, { name: m.name, email: m.email, role: newRole });
      toast.success("Função atualizada.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remover ${m.name} da equipe? O acesso dele à sua conta é revogado na hora.`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      toast.success("Membro removido.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5 animate-in-up max-w-2xl" data-testid="team-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Users className="h-6 w-6 text-sky-600" /> Minha Equipe</h1>
        <p className="text-sm text-slate-500">Dê acesso à sua conta para técnicos e gerentes — cada um entra com o próprio e-mail e senha.</p>
      </div>

      <Card className="border-sky-200 bg-sky-50">
        <CardContent className="p-4 text-xs text-sky-900 space-y-1.5">
          <p className="flex gap-1.5"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span><b>Técnico:</b> vê e edita Ordens de Serviço, agenda, manutenções e consulta orçamentos. Não vê custos, relatórios nem configurações.</span></p>
          <p className="flex gap-1.5"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span><b>Gerente:</b> acesso quase completo, exceto plano/pagamentos e gestão da equipe.</span></p>
          <p className="flex gap-1.5"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span>O membro precisa criar a conta dele na página de cadastro usando <b>o mesmo e-mail</b> cadastrado aqui. Ao entrar, ele verá os dados da sua empresa conforme a função.</span></p>
          {limit >= 0 && (
            <p className="font-semibold" data-testid="team-limit-info">
              {limit === 0
                ? "Seu plano atual não inclui membros de equipe — faça upgrade para adicionar."
                : `Vagas do seu plano: ${used} de ${limit} usadas.`}
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <form onSubmit={add} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nome</Label><Input className="h-12" data-testid="team-add-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Nome do membro" /></div>
              <div className="space-y-1.5"><Label>E-mail</Label><Input className="h-12" type="email" data-testid="team-add-email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="email@dominio.com" /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Função</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="h-12" data-testid="team-add-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tecnico">Técnico (limitado)</SelectItem>
                  <SelectItem value="gerente">Gerente (quase completo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" disabled={saving} data-testid="team-add-btn">
              <UserPlus className="h-4 w-4 mr-2" /> {saving ? "Adicionando..." : "Adicionar membro"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="grid gap-3">
        {members.length === 0 && (
          <Card className="border-slate-200"><CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="team-empty">
            Nenhum membro na equipe ainda.
          </CardContent></Card>
        )}
        {members.map((m) => (
          <Card key={m.id} className="border-slate-200 shadow-sm" data-testid={`team-member-${m.id}`}>
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold truncate">{m.name}</p>
                <p className="text-sm text-slate-500 truncate">{m.email}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                  <SelectTrigger className="h-10 w-32" data-testid={`team-role-${m.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tecnico">Técnico</SelectItem>
                    <SelectItem value="gerente">Gerente</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className="h-10 w-10 text-red-500 hover:bg-red-50" onClick={() => remove(m)} data-testid={`team-del-${m.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
